import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import FormForeignState, { ForeignStateForm } from 'system/geographic/components/state/form';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonForeignStateCrumbs } from '../constants';
import { HttpResponse } from 'shell/models/http-response';
import { ForeignState } from 'shell/models/state';
import { toast } from 'react-toastify';

const ForeignStateEditPage = (): React.ReactElement => {
  const { put: updateForeignState } = useHttpFetch(`${__MUNICIPALITY_API_URL__}/geo/stato`);

  const params = useParams();

  const {
    get: getForeignState,
    data,
    error,
    loading,
  } = useHttpFetch<HttpResponse<ForeignState>>(
    `${__MUNICIPALITY_API_URL__}/geo/stato/${params.id}`
  );

  useEffect(() => {
    getForeignState();
  }, []);

  const foreignForeignState: ForeignStateForm = useMemo(() => {
    const foreignStatePayload = data?.payload;
    if (!foreignStatePayload || !!error) {
      return {};
    }
    return {
      acronym: foreignStatePayload.key.codice,
      code: foreignStatePayload.codIstat,
      name: foreignStatePayload.nome,
      exonym: foreignStatePayload.esonimo,
      endonym: foreignStatePayload.endonimo,
      citizenship: foreignStatePayload.codiceCittadinanza?.toString(),
      citizenMale: foreignStatePayload.nomeCittadino,
      citizenFemale: foreignStatePayload.nomeCittadina,
      fiscalCode: foreignStatePayload.belfiore,
      isoCode: foreignStatePayload.iso,
      capitalCode: foreignStatePayload.codiceCapitale?.toString(),
      capitalName: foreignStatePayload.nome,
      releaseUE: foreignStatePayload.flagRilascioCI === '1' ? true : false,
      ue: foreignStatePayload.flagUnioneEuropea,
      license: foreignStatePayload.siglaMotorizzazione,
    };
  }, [data, error]);

  const navigate = useNavigate();

  const onSubmit = useCallback(
    (formValues: ForeignStateForm | undefined) => {
      updateForeignState(`/${formValues?.acronym}`, {
        codIstat: formValues?.code,
        nome: formValues?.name,
        esonimo: formValues?.exonym,
        endonimo: formValues?.endonym,
        codiceCittadinanza: formValues?.citizenship,
        nomeCittadino: formValues?.citizenMale,
        nomeCittadina: formValues?.citizenFemale,
        belfiore: formValues?.fiscalCode,
        iso: formValues?.isoCode,
        codiceCapitale: formValues?.capitalCode,
        flagRilascioCI: formValues?.releaseUE ? '1' : '0',
        flagUnioneEuropea: formValues?.ue,
        siglaMotorizzazione: formValues?.license,
        cmnAssegn: 0,
        key: {
          codice: formValues?.acronym,
        },
      }).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/sistema/geografiche/estero/stato');
      });
    },
    [updateForeignState, navigate]
  );

  return (
    <Fragment>
      <Breadcrumbs items={[...CommonForeignStateCrumbs, { name: 'Modifica Stato Estero' }]} />
      {loading || error || !data ? undefined : (
        <FormForeignState
          title="Modifica Stato Estero"
          onOk={onSubmit}
          initialValues={foreignForeignState}
          isEdit
          onCancel={() => {
            navigate('/sistema/geografiche/estero/stato');
          }}
        />
      )}
    </Fragment>
  );
};

ForeignStateEditPage.displayName = 'Foreign State edit page component';

export default ForeignStateEditPage;
